/**
 * AllPrArticlesPage
 *
 * Every article labelled with the PR (parasite rate) topic. This is the top
 * entry in the PR Literature Review sidebar and the section's broadest list.
 *
 * It is a "score page": it renders the shared minimum-PR-score slider above the
 * table so reviewers can raise the classifier-confidence floor. Unlike Needs
 * Rereview / New Finds, it has no inherent score floor (the slider starts at 0)
 * and applies no PR-coverage flag — it simply pins the PR topic.
 */

import { useMemo } from "react"
import PrLiteratureReviewShell from "@/components/pr-literature-review/PrLiteratureReviewShell"
import JournalArticlesTable, {
  buildCollectionFilterOptions,
} from "@/components/journal-articles/JournalArticlesTable"
import {
  usePrLiteratureReviewPage,
  stripLocationColumns,
} from "@/components/pr-literature-review/usePrLiteratureReviewPage"
import { getColumnGroupsForViewMode } from "@/components/pr-literature-review/columns"
import PrScoreSlider from "@/components/pr-literature-review/PrScoreSlider"

/** Browser tab title and page heading. */
const PAGE_TITLE: string = "All PR Articles"

/** Short description shown under the page heading. */
const PAGE_DESCRIPTION: string =
  "Browse every article labelled with the PR (parasite rate) topic"

/**
 * The All PR Articles page.
 *
 * @returns The rendered page element.
 */
export default function AllPrArticlesPage(): React.JSX.Element {
  const page = usePrLiteratureReviewPage({
    sidebarOption: "all-pr-articles",
    // Highest-PR-score-first is the most useful default review order.
    defaultSort: { key: "scorePRTopic", direction: "desc" },
  })

  const filterOptions = useMemo(
    () => buildCollectionFilterOptions(page.collections),
    [page.collections]
  )

  const visibleColumns = useMemo(
    () => stripLocationColumns(page.visibleColumns),
    [page.visibleColumns]
  )

  return (
    <PrLiteratureReviewShell
      title={PAGE_TITLE}
      description={PAGE_DESCRIPTION}
      showDetailsPanel
      searchValue={page.searchQuery}
      onSearchChange={page.setSearchQuery}
    >
      <PrScoreSlider
        scoreMin={page.prScoreMin}
        scoreFloor={page.prScoreFloor}
        onScoreCommit={page.setPrScoreMin}
      />
      <JournalArticlesTable
        page={page}
        filterOptions={filterOptions}
        visibleColumns={visibleColumns}
        columnGroupsForViewMode={getColumnGroupsForViewMode}
      />
    </PrLiteratureReviewShell>
  )
}
